import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import { createPortal } from "react-dom";

const INTERACTIVE_CLOSE_DELAY_MS = 180;

export type TooltipCardProps = {
  /** Content shown inside the tooltip card (plain text or React node). */
  content: ReactNode;
  /** Element that triggers the tooltip on hover. */
  children: ReactNode;
  /** Extra class for the tooltip popover. */
  containerClassName?: string;
  /** Extra class for the trigger wrapper (e.g. cursor-pointer font-bold). */
  triggerClassName?: string;
  /** Offset from pointer (default 12). */
  offset?: number;
  /**
   * When true, the card is anchored under the trigger (not cursor-following),
   * accepts clicks inside the card, and stays open while the pointer moves
   * from the trigger into the card.
   */
  interactive?: boolean;
};

/**
 * Tooltip card that follows the mouse when the trigger is hovered.
 * Reusable, design-token styled. Inspired by Aceternity UI Tooltip Card.
 */
export function TooltipCard({
  content,
  children,
  containerClassName = "",
  triggerClassName = "",
  offset = 12,
  interactive = false,
}: TooltipCardProps) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(
    (e: ReactMouseEvent<HTMLSpanElement>) => {
      clearCloseTimer();
      if (interactive && triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect();
        const cardWidth = 280;
        const x = Math.max(
          8,
          Math.min(r.left, window.innerWidth - cardWidth - 8),
        );
        const y = r.bottom + 8;
        setPosition({ x, y });
      } else {
        setPosition({ x: e.clientX + offset, y: e.clientY + offset });
      }
      setVisible(true);
    },
    [clearCloseTimer, interactive, offset],
  );

  const handleTriggerLeave = useCallback(() => {
    if (interactive) {
      closeTimerRef.current = setTimeout(() => {
        setVisible(false);
        closeTimerRef.current = null;
      }, INTERACTIVE_CLOSE_DELAY_MS);
    } else {
      setVisible(false);
    }
  }, [interactive]);

  const handleTooltipEnter = useCallback(() => {
    clearCloseTimer();
  }, [clearCloseTimer]);

  const handleTooltipLeave = useCallback(() => {
    setVisible(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      setPosition({ x: e.clientX + offset, y: e.clientY + offset });
    },
    [offset],
  );

  useEffect(() => {
    if (!visible || interactive) return;
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [visible, interactive, handleMouseMove]);

  useEffect(() => {
    return () => clearCloseTimer();
  }, [clearCloseTimer]);

  const pointerClass = interactive
    ? "pointer-events-auto"
    : "pointer-events-none";

  const tooltip = visible ? (
    <div
      role="tooltip"
      className={`${pointerClass} fixed left-0 top-0 z-[100] w-max max-w-[280px] rounded-xl border border-border bg-card p-3 text-sm text-card-foreground shadow-lg transition-opacity duration-150 ${containerClassName}`}
      style={{
        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
      }}
      onMouseEnter={interactive ? handleTooltipEnter : undefined}
      onMouseLeave={interactive ? handleTooltipLeave : undefined}
    >
      {content}
    </div>
  ) : null;

  return (
    <>
      {/* A span, not a div: triggers sit inside paragraphs, and a block
          element there makes the HTML parser split the <p>, which breaks
          hydration of the prerendered page. */}
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleTriggerLeave}
        className={`inline-flex cursor-default ${triggerClassName}`.trim()}
      >
        {children}
      </span>
      {typeof document !== "undefined" && tooltip
        ? createPortal(tooltip, document.body)
        : null}
    </>
  );
}

export const Tooltip = TooltipCard;

export const HoverCard = TooltipCard;

export type HoverCardProps = TooltipCardProps;
